<template>
    <ConfigCard :icon="Db2DatabaseIcon" title="Custom storage">
        <div class="mb-4 flex flex-col gap-2">
            <button
                v-for="provider in orderedProviders"
                :key="provider.key"
                class="flex w-full min-w-0 items-center justify-between gap-3 bg-surface-card px-4 py-2.5 transition-colors hover:bg-[#eceef1] dark:bg-muted"
                @click="$emit('selectProvider', provider.key)"
            >
                <p class="min-w-0 text-left text-sm text-muted-foreground">
                    {{ apiToken.settings?.storage_provider ? `Edit ${provider.name} storage` : `Set up ${provider.name} storage` }}
                </p>
                <div class="flex h-10 shrink-0 items-center justify-center">
                    <img
                        :src="provider.img"
                        :alt="provider.name"
                        class="object-contain"
                        :style="figmaSize(provider.key)"
                    />
                </div>
            </button>
        </div>

        <p class="mt-auto max-w-xs text-xs leading-4 text-muted-foreground">
            <span v-if="!apiToken?.settings?.storage_provider">
                Currently using default storage.<br> Configure your own Google Cloud, Amazon S3, Cloudflare R2, or DigitalOcean Spaces.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'gcs'">
                Using custom Google Cloud storage.<br> Detach current storage to switch providers.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'aws'">
                Using Amazon S3 storage.<br> Detach current storage to switch providers.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'cloudflare'">
                Using Cloudflare R2 storage.<br> Detach current storage to switch providers.
            </span>
            <span v-else-if="apiToken?.settings?.storage_provider === 'digitalocean'">
                Using DigitalOcean Spaces storage.<br> Detach current storage to switch providers.
            </span>
            For more information,
            <a
                href="https://docs.topol.io/guide/custom-storage.html"
                target="_blank"
                class="font-semibold underline text-new-blue-500 hover:text-new-blue-600"
            >
                check out our documentation</a
            >.
        </p>
    </ConfigCard>
</template>

<script lang="ts" setup>
import { PropType, computed } from 'vue';
import ConfigCard from './ConfigCard.vue';
import Db2DatabaseIcon from './icons/Db2DatabaseIcon.vue';
import IToken from '@/types/Plugin/IToken';
import { allProviders } from '@/Pages/PluginSettings/data/storageProviders';

const props = defineProps({
    apiToken: {
        type: Object as PropType<IToken>,
        required: true,
    },
});

defineEmits<{
    selectProvider: [provider: 'aws' | 'cloudflare' | 'gcs' | 'digitalocean'];
}>();

const visibleProviders = computed(() => {
    const currentProvider = props.apiToken?.settings?.storage_provider;

    if (currentProvider) {
        return allProviders.filter((p) => p.key === currentProvider);
    }

    return allProviders;
});

const providerOrder: Record<
    'cloudflare' | 'gcs' | 'aws' | 'digitalocean',
    number
> = {
    cloudflare: 0,
    gcs: 1,
    aws: 2,
    digitalocean: 3,
};

const orderedProviders = computed(() =>
    [...visibleProviders.value].sort(
        (a, b) => providerOrder[a.key] - providerOrder[b.key]
    )
);

const figmaSize = (key: 'gcs' | 'aws' | 'cloudflare' | 'digitalocean') => {
    switch (key) {
        case 'gcs':
            return { width: '102px', height: '40px' };
        case 'aws':
            return { width: '32px', height: '32px' };
        case 'cloudflare':
            return { width: '73px', height: '24px' };
        case 'digitalocean':
            return { width: '102px', height: '40px' };
    }
};
</script>
